import { ToolType, Tool_V1 } from 'lib/assets/types/tool_v1'
import { Segment } from './segments-viewer'

function deg2rad(deg) {
  return (deg / 180) * Math.PI
}

function trimSegmentFromTop(segment: Segment, newHeight: number): Segment {
  const slope = (segment.upperDiameter - segment.lowerDiameter) / segment.height
  const trimmed = {
    ...segment,
    height: newHeight,
    upperDiameter: segment.lowerDiameter + slope * newHeight,
  }
  delete trimmed.upperRoundingRadius // can't trim a radii
  return trimmed
}

/**
 * Generate Segments for tool geometry ordered from top (shank) to bottom (tip).
 */
export function geometryToSegments(
  tool: Tool_V1,
  protrudingOnly: boolean = true,
): Segment[] {
  if (!tool || !tool.type || tool.geometry_cuttingDiameter === undefined) {
    return []
  }

  const drillLikeType: ToolType[] = [
    'drill',
    'center drill',
    'spot drill',
    'counter sink',
    'chamfer mill',
  ]

  const isDrillLike = drillLikeType.includes(tool.type)

  const ballTypes: ToolType[] = ['ball end mill', 'lollipop mill']
  const isBallTip = ballTypes.includes(tool.type)

  const isSlotMill = tool.type == 'slot mill'
  const isCornerRounding = tool.type == 'radius mill'
  const isDoveTail = tool.type == 'dovetail mill'

  const segments: Segment[] = []
  let usedLength = 0

  // NOTE: Setting colors here is a bit of a hack. We should use groups/subgroups for
  // this and let SegmentsViewer control the colors based on that (and keep track of
  // the highlighted segment)
  const fluteColor = '#e6e0a1'
  const shoulderColor = '#b7c4c9'

  // 1) Tip - drill like
  if (isDrillLike) {
    const D1 = tool.geometry_tipDiameter || 0
    const D2 = tool.geometry_cuttingDiameter || 0
    const L = tool.geometry_fluteLength || 0
    const halfAngle =
      deg2rad(tool.geometry_tipAngle || tool.geometry_taperAngle * 2 || 0) / 2
    const taperHeight = (D2 - D1) / (2 * Math.tan(halfAngle))
    segments.push(
      {
        height: taperHeight,
        lowerDiameter: D1,
        upperDiameter: D2,
        fillColor: fluteColor,
      },
      {
        height: L - taperHeight,
        lowerDiameter: D2,
        upperDiameter: D2,
        fillColor: fluteColor,
      },
    )

    usedLength += L
  }

  // 1) Tip - other types
  else {
    let tipD = Math.max(
      tool.geometry_tipDiameter || 0.0,
      tool.geometry_cuttingDiameter || 0.0,
    )

    let tipR = isBallTip ? tipD / 2.0 : tool.geometry_cornerRadius || 0.0
    if (isCornerRounding) {
      tipR = -tipR
      tipD += -tipR * 2
    }
    const tipL = Math.max(
      tool.geometry_tipLength || 0.0,
      tool.geometry_fluteLength || 0.0,
    )

    let tipAngle = deg2rad(
      tool.geometry_tipAngle || (tool.geometry_taperAngle || 0.0) * 2.0,
    )
    if (isDoveTail) {
      tipAngle = -tipAngle
    }

    segments.push({
      height: tipL,
      lowerRoundingRadius: tipR,
      lowerDiameter: tipD,
      upperDiameter: (tipD / 2 + Math.tan(tipAngle / 2) * tipL) * 2,
      ...(isSlotMill ? { upperRoundingRadius: tipR } : {}),
      fillColor: fluteColor,
    })
    usedLength += tipL
  }

  // 2) Shoulder (non-cutting)
  const shoulderD = isSlotMill
    ? tool.geometry_shaftDiameter
    : Math.max(
        tool.geometry_shoulderDiameter || 0.0,
        segments[segments.length - 1].upperDiameter || 0.0,
      )
  const shoulderL =
    Math.max(tool.geometry_shoulderLength || 0.0, usedLength) - usedLength
  if (shoulderL > 0.0) {
    segments.push({
      height: shoulderL,
      lowerDiameter: shoulderD,
      upperDiameter: shoulderD, // maybe: + Math.tan(tipAngle) * shoulderL,
      fillColor: shoulderColor,
    })
    usedLength += shoulderL
  }

  // 3) Custom Shaft segments
  let shaftD = Math.max(tool.geometry_shaftDiameter || 0.0)
  if (tool.shaft?.segments && tool.shaft.segments.length > 0) {
    for (const seg of tool.shaft.segments) {
      const remainingL = tool.geometry_protrudingLength - usedLength
      if (protrudingOnly && remainingL <= seg.height) {
        const newseg = trimSegmentFromTop(seg, remainingL)
        segments.push(newseg)
        usedLength += newseg.height
      } else {
        segments.push({ ...seg })
        usedLength += seg.height
      }
    }
    shaftD = segments[segments.length - 1].upperDiameter
  }

  // 4) Fixed Shaft
  const shaftL =
    Math.max(tool.geometry_protrudingLength || 0.0, usedLength) - usedLength
  if (shaftL > 0.0) {
    segments.push({
      height: shaftL,
      lowerDiameter: shaftD,
      upperDiameter: shaftD,
    })
    usedLength += shoulderL
  }

  // 5) Remaining straight shank up to the overall length
  if (!protrudingOnly) {
    const remainingL = tool.geometry_overallLength || 0.0 - usedLength
    segments.push({
      height: remainingL,
      lowerDiameter: shaftD,
      upperDiameter: shaftD,
    })
  }

  // Finally, reverse so the first entry is the top of the tool and the last is the tip
  return segments.reverse()
}
